"use client";

import type { CSSProperties } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";
import NetworkMarquee from "./hero/NetworkMarquee";
import HeroCarousel from "./hero/HeroCarousel";
import HighlightedTitle from "./HighlightedTitle";
import { useCountUp } from "../hooks/useCountUp";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const STATS = [
  { key: "years", value: 20, suffix: "+" },
  { key: "stores", value: 100, suffix: "+" },
  { key: "brands", value: 4, suffix: "" },
] as const;

const DOT_GRID: CSSProperties = {
  backgroundImage: "radial-gradient(rgba(15, 27, 45, 0.12) 1px, transparent 1px)",
  backgroundSize: "24px 24px",
};

const enter = (delayMs: number): CSSProperties => ({ animationDelay: `${delayMs}ms` });
const ENTER_CLASS = "animate-hero-fade-up motion-reduce:animate-none";

function StatItem({
  value,
  suffix,
  label,
  reducedMotion,
}: {
  value: number;
  suffix: string;
  label: string;
  reducedMotion: boolean;
}) {
  const current = useCountUp(value, { disabled: reducedMotion });
  return (
    <div className="flex flex-col-reverse">
      <dt className="mt-2 text-[15px] text-brand-muted-light">{label}</dt>
      <dd className="text-[44px] font-bold leading-none tabular-nums text-brand-navy">
        {current}
        {suffix}
      </dd>
    </div>
  );
}

export default function Hero() {
  const router = useRouter();
  const t = useTranslations("home");
  const locale = useLocale();
  const reducedMotion = usePrefersReducedMotion();

  return (
    <section id="about" className="relative w-full overflow-hidden bg-brand-cream" style={DOT_GRID}>
      <div className="mx-auto flex max-w-[1240px] flex-wrap items-center gap-14 px-8 pb-9 pt-6">
        <div className="min-w-0 flex-[1_1_480px]">
          <span
            className={`${ENTER_CLASS} inline-flex items-center gap-2 rounded-full border-[1.5px] border-brand-navy px-4 py-1.5 text-[15px] font-semibold uppercase tracking-[0.04em] text-brand-navy`}
            style={enter(50)}
          >
            <span
              aria-hidden="true"
              className="h-2 w-2 animate-home-blink rounded-full bg-brand-accent motion-reduce:animate-none"
            />
            {t("subtitle")}
          </span>

          <h1
            className={`${ENTER_CLASS} mt-6 text-[clamp(44px,6vw,78px)] font-bold leading-[1.02] tracking-[-0.02em] text-brand-navy`}
            style={enter(150)}
          >
            <HighlightedTitle title={t("title")} highlight={t("titleHighlight")} />
          </h1>

          <p
            className={`${ENTER_CLASS} mt-6 max-w-[540px] text-lg text-brand-muted-light sm:text-xl`}
            style={enter(280)}
          >
            {t("description")}
          </p>

          <div
            className={`${ENTER_CLASS} mt-8 flex flex-wrap items-center gap-x-8 gap-y-4`}
            style={enter(400)}
          >
            <button
              type="button"
              onClick={() => router.push(`/${locale}/login`)}
              className="inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-4 text-lg font-bold text-brand-navy shadow-[0_6px_0_#0F1B2D] transition-[transform,box-shadow] duration-200 hover:translate-y-0.5 hover:shadow-[0_4px_0_#0F1B2D] active:translate-y-1 active:shadow-[0_2px_0_#0F1B2D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-4 focus-visible:ring-offset-brand-cream"
            >
              {t("button")}
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </button>
            <a
              href="#active-tenders"
              className="rounded text-lg font-semibold text-brand-navy underline decoration-2 underline-offset-[6px] hover:decoration-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-4 focus-visible:ring-offset-brand-cream"
            >
              {t("secondaryCta")}
            </a>
          </div>

          <dl
            className={`${ENTER_CLASS} mt-12 grid max-w-[540px] grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3`}
            style={enter(550)}
          >
            {STATS.map((stat) => (
              <StatItem
                key={stat.key}
                value={stat.value}
                suffix={stat.suffix}
                label={t(`stats.${stat.key}`)}
                reducedMotion={reducedMotion}
              />
            ))}
          </dl>
        </div>

        <div className={`${ENTER_CLASS} min-w-0 max-w-[540px] flex-[1_1_440px]`} style={enter(400)}>
          <HeroCarousel />
        </div>
      </div>

      <div className={ENTER_CLASS} style={enter(600)}>
        <NetworkMarquee />
      </div>
    </section>
  );
}
